import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import apiClient from "../api/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import "./CalculatorPromo.css";

const POINTS = [
  {
    icon: "fa-weight-scale",
    title: "Know where you stand",
    text: "Your BMI and body metrics, read against healthy ranges instead of guesswork.",
  },
  {
    icon: "fa-fire",
    title: "Know what to eat",
    text: "Your BMR and daily calories, split into protein, carbs and fat for your goal.",
  },
  {
    icon: "fa-arrow-trend-up",
    title: "Train on real numbers",
    text: "Set targets from your own figures, then adjust them as your training changes.",
  },
];

/**
 * Promotes the BMI and BMR calculators on the homepage.
 *
 * The tool is gated on the `bmi_calculator` package feature, so the button
 * follows the visitor's access: straight in if they have it, to the packages
 * page if they do not.
 */
const CalculatorPromo = () => {
  const { t } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (authLoading) return undefined;

    if (!user) {
      setHasAccess(false);
      setChecking(false);
      return undefined;
    }

    let cancelled = false;
    setChecking(true);

    apiClient
      .get("/features/bmi_calculator/access")
      .then(({ data }) => {
        if (!cancelled) setHasAccess(Boolean(data.has_access));
      })
      .catch(() => {
        // Treat an unreadable check as no access: the packages page is the
        // safe landing spot, and the calculator route guards itself anyway.
        if (!cancelled) setHasAccess(false);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return (
    <section className="hpa-calc-promo" aria-labelledby="hpa-calc-promo-title">
      <div className="hpa-calc-promo__inner">
        <div className="hpa-calc-promo__copy">
          <span className="hpa-calc-promo__kicker">{t("Included with your package")}</span>
          <h2 id="hpa-calc-promo-title">{t("BMI AND BMR CALCULATORS")}</h2>
          <p>
            {t("Training works better when it is built on your own numbers. Calculate your body metrics and your daily energy needs, then train and eat against figures that are actually yours.")}
          </p>

          <ul className="hpa-calc-promo__points">
            {POINTS.map((point) => (
              <li key={point.title}>
                <span aria-hidden="true"><i className={`fa-solid ${point.icon}`} /></span>
                <div>
                  <strong>{t(point.title)}</strong>
                  <small>{t(point.text)}</small>
                </div>
              </li>
            ))}
          </ul>

          <div className="hpa-calc-promo__actions">
            {checking && !authLoading ? (
              <span className="hpa-calc-promo__checking">{t("Checking your access...")}</span>
            ) : hasAccess ? (
              <Link to="/bmi-calculator" className="btn btn-skew hpa-calc-promo__btn">
                <span>{t("Open the calculators")}</span>
              </Link>
            ) : (
              <Link to="/packages" className="btn btn-skew hpa-calc-promo__btn">
                <span>{t("Request Access")}</span>
              </Link>
            )}
            {!hasAccess && !checking && (
              <small className="hpa-calc-promo__note">
                {t("Available on any package that includes the BMI & Body Metrics Calculator.")}
              </small>
            )}
          </div>
        </div>

        <div className="hpa-calc-promo__visual" aria-hidden="true">
          <div className="hpa-calc-promo__dial">
            <span className="hpa-calc-promo__dial-value">22.4</span>
            <span className="hpa-calc-promo__dial-label">BMI</span>
          </div>
          <div className="hpa-calc-promo__stat">
            <strong>1,780</strong>
            <span>{t("kcal BMR")}</span>
          </div>
          <div className="hpa-calc-promo__macros">
            <span style={{ "--share": "30%" }}><em>30%</em>{t("Protein")}</span>
            <span style={{ "--share": "45%" }}><em>45%</em>{t("Carbs")}</span>
            <span style={{ "--share": "25%" }}><em>25%</em>{t("Fat")}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalculatorPromo;
